"use client";

import { JSX, useState } from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { fmtNum } from "@/lib/format";
import { useViewerLanguage } from "@/lib/i18n";
import { useTween } from "@/lib/hooks/use-tween";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import type { ModulePricingTiersData } from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";

type Billing = "monthly" | "yearly";

const TIER_MIN_WIDTH = 240;

function PriceTag({ value, suffix }: { value: number; suffix: string }) {
  const v = useTween(value, { duration: 500 });
  const { intlLocale } = useViewerLanguage();
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-3xl font-medium tracking-tight tabular-nums">
        {fmtNum(Math.round(v), 0, intlLocale)} €
      </span>
      <span className="text-xs text-[var(--muted)]">{suffix}</span>
    </div>
  );
}

export default function ModulePricingTiers({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModulePricingTiersData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const tiers = data.tiers;
  const features = data.features;
  const yearlyDiscount = data.yearlyDiscount ?? 0.2;
  const defaultBilling = data.defaultBilling ?? "yearly";
  const priceSuffix = data.priceSuffix ?? t("per user / month");
  const recommendedLabel = data.recommendedLabel ?? t("Recommended");

  const hasToggle = yearlyDiscount > 0;
  const [billing, setBilling] = useState<Billing>(
    hasToggle ? defaultBilling : "monthly",
  );
  const factor = billing === "yearly" ? 1 - yearlyDiscount : 1;

  return (
    <div
      className={cn("w-full", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      {hasToggle && (
        <div className="mb-5 flex justify-start">
          <SegmentTabs
            value={billing}
            onValueChange={(v) => v !== null && setBilling(v as Billing)}
            options={[
              { value: "monthly", label: t("Monthly") },
              {
                value: "yearly",
                label: (
                  <>
                    {t("Yearly")}
                    <span className="ml-1.5 text-xs text-[var(--muted)]">
                      −{fmtNum(yearlyDiscount * 100, 0, intlLocale)} %
                    </span>
                  </>
                ),
              },
            ]}
          />
        </div>
      )}

      <div className="relative">
        <div className="overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div
            className="grid grid-rows-1 gap-3"
            style={{
              gridTemplateColumns: `repeat(${tiers.length}, minmax(${TIER_MIN_WIDTH}px, 1fr))`,
              minWidth: "max-content",
            }}
          >
            {tiers.map((tier) => {
              const price = tier.price * factor;
              return (
                <div
                  key={tier.id}
                  className={cn(
                    "relative flex flex-col rounded-xl p-4 ring-1 transition-colors",
                    tier.recommended
                      ? "bg-black/[0.02] ring-black/[0.14] dark:bg-white/[0.04] dark:ring-white/[0.16]"
                      : "ring-black/[0.08] hover:ring-black/[0.14] dark:ring-white/[0.10] dark:hover:ring-white/[0.16]",
                  )}
                >
                  {tier.recommended && (
                    <div className="absolute inset-x-0 top-0 flex h-8 items-center border-b border-dashed border-[var(--border-dashed)] rounded-t-xl bg-[repeating-linear-gradient(135deg,transparent_0_6px,rgba(0,0,0,0.03)_6px_7px)] dark:bg-[repeating-linear-gradient(135deg,transparent_0_6px,rgba(255,255,255,0.05)_6px_7px)] px-4">
                      <span className="text-xs text-[var(--fg)]">
                        {recommendedLabel}
                      </span>
                    </div>
                  )}

                  <div className={cn(tier.recommended && "pt-7")}>
                    <div
                      className={`text-[14px] font-medium ${MODULE_LABEL_WRAP.cell}`}
                    >
                      {stripInlineMarkdown(tier.name)}
                    </div>
                    <div
                      className={`mt-0.5 text-xs text-[var(--muted)] ${MODULE_LABEL_WRAP.cell}`}
                    >
                      {stripInlineMarkdown(tier.description)}
                    </div>
                    <div className="mt-4">
                      <PriceTag value={price} suffix={priceSuffix} />
                    </div>
                    <div className="mt-1 h-4 text-[11px] text-[var(--subtle)]">
                      {billing === "yearly"
                        ? t("{amount} € billed yearly", {
                            amount: fmtNum(price * 12, 0, intlLocale),
                          })
                        : t("Cancel anytime")}
                    </div>
                  </div>

                  <div className="my-4 border-t border-dashed border-[var(--border-dashed)]" />

                  <ul className="flex-1 space-y-2">
                    {features.map((f) => {
                      const inc = tier.included.includes(f);
                      return (
                        <li
                          key={f}
                          className={cn(
                            "flex items-center gap-2 text-[13px]",
                            inc ? "text-[var(--fg)]" : "text-[var(--subtle)]",
                          )}
                        >
                          {inc ? (
                            <AnimatedIcon
                              name="check"
                              fallback={Check}
                              className="h-3 w-3 shrink-0"
                              strokeWidth={2}
                              style={{ color: "var(--module-accent)" }}
                            />
                          ) : (
                            <Minus
                              className="h-3 w-3 shrink-0"
                              strokeWidth={2}
                              style={{ color: "var(--subtle)" }}
                            />
                          )}
                          <span className={MODULE_LABEL_WRAP.cell}>
                            {stripInlineMarkdown(f)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-8 rounded-l-xl"
          style={{
            background:
              "linear-gradient(to right, var(--card, #ffffff), transparent)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-8 rounded-r-xl"
          style={{
            background:
              "linear-gradient(to left, var(--card, #ffffff), transparent)",
          }}
        />
      </div>
    </div>
  );
}
